import React, { FC, useState } from "react";
import { Button, Form, Modal, FormInstance } from "antd";
import {
  MatterFormMode,
  MatterNonExternalFieldsPreUpdateMatterCreate,
  MatterType,
  OrganisationalUnit,
  PricingLevel,
  PricingTarget,
  PricingType,
  Template,
} from "../../data-types";
import { MatterStartingOptions } from "./starting-point-selection";
import { formValuesToData } from "../matter-creation-or-editing-form";
import { MatterFormValuesToData } from "../matter-creation-or-editing-form/types";
import { cascaderValueFromFeeEarner } from "../../utils";
import { LibraryMatterType, LibraryMatterTypeObj } from "~/hooks/use-paginated-matter-query";
import { ParentPhaseSelect } from "./phase-select";
import { pricingStructureToCascaderValue } from "../pricing-structure-options";

const orgUnitCascaderFromValue = (ou: OrganisationalUnit) => {
  if (!ou?.parent?.id) {
    return [ou?.id];
  }
  return [ou?.parent?.id, ou?.id];
};

export type CreateMatterProps = {
  loading?: boolean;
  onCreate: (matter: {
    title: string;
    externalReference?: string;
    clientId: number | null;
    pricingLevel?: PricingLevel;
    pricingType?: PricingType;
    templateId?: number;
    practiceAreaId: number;
    multiDisciplinaryMatter?: boolean;
  }) => void;
  createMultiDiscMatter: (matter: { title: string; clientId: number; phases: string[] }) => void;
  onCancel?: () => void;
  renderStartingPoint: (arg: { form: FormInstance }) => React.ReactNode;
  renderMatterDetailsForm: (arg: {
    showPricingOptions: boolean;
    form: FormInstance;
  }) => React.ReactNode;
  renderSelectTemplateForm: (arg: {
    onSelect: (template: Template) => void;
    libraryMatterType: LibraryMatterType;
    form: FormInstance<any>;
  }) => React.ReactNode;
  renderMultiDisc: (arg: { form: FormInstance }) => React.ReactNode;
  parentMatterId?: number;
  labelOveride?: string;
};

enum Screens {
  MatterStartingOption = 0,
  MatterTemplateSelection = 1,
  matterDetailsSelection = 2,
  multiDisciplinaryMatter = 3,
}
const isFirstScreen = (screen: Screens) => screen === Screens.MatterStartingOption;
const isLastScreen = (screen: Screens) => screen === 2 || screen === 3;

const getNextScreen = (
  currentScreen: Screens,
  MatterStartingOption: MatterStartingOptions
): Screens => {
  if (currentScreen === Screens.MatterStartingOption) {
    if (MatterStartingOption === MatterStartingOptions.Bespoke) {
      return Screens.matterDetailsSelection;
    }
    if (MatterStartingOption === MatterStartingOptions.MultiDisciplinary) {
      return Screens.multiDisciplinaryMatter;
    }
    if (MatterStartingOption === MatterStartingOptions.Template) {
      return Screens.MatterTemplateSelection;
    }
    if (MatterStartingOption === MatterStartingOptions.HistoricalMatter) {
      return Screens.MatterTemplateSelection;
    }
  } else if (currentScreen === Screens.MatterTemplateSelection) {
    return Screens.MatterTemplateSelection;
  }
  return currentScreen;
};

const getPreviousScreen = (
  currentScreen: Screens,
  MatterStartingOption: MatterStartingOptions
): Screens => {
  if (
    currentScreen === Screens.matterDetailsSelection ||
    currentScreen === Screens.multiDisciplinaryMatter
  ) {
    return MatterStartingOption === MatterStartingOptions.Bespoke ||
      MatterStartingOption === MatterStartingOptions.MultiDisciplinary
      ? Screens.MatterStartingOption
      : Screens.MatterTemplateSelection;
  } else if (currentScreen === Screens.MatterTemplateSelection) {
    return Screens.MatterStartingOption;
  }
  return currentScreen;
};
export const CreateMatter: FC<CreateMatterProps> = ({
  onCreate,
  onCancel,
  createMultiDiscMatter,
  renderStartingPoint,
  renderMatterDetailsForm,
  renderSelectTemplateForm,
  renderMultiDisc,
  loading,
  labelOveride,
  parentMatterId,
}) => {
  const [displayModal, setDisplayModal] = useState(false);

  const [screen, setScreen] = useState(Screens.MatterStartingOption);
  const [form] = Form.useForm();

  const resetState = () => {
    form.resetFields();
    setScreen(Screens.MatterStartingOption);
  };

  const MatterStartingOption = form.getFieldValue("startingPoint") as MatterStartingOptions;

  const ScreensFunctions: Record<Screens, { render: () => React.ReactNode }> = {
    [Screens.MatterStartingOption]: {
      render: () =>
        renderStartingPoint({
          form,
        }),
    },
    [Screens.MatterTemplateSelection]: {
      render: () => {
        MatterStartingOption;

        return renderSelectTemplateForm({
          libraryMatterType:
            MatterStartingOption === MatterStartingOptions.HistoricalMatter
              ? LibraryMatterTypeObj.HISTORICAL
              : LibraryMatterTypeObj.TEMPLATE,
          onSelect: ({
            id: templateId,
            client,
            pricingType,
            pricingLevel,
            pricingTarget,
            organisationalUnit,
            leadFeeEarner,
          }: Template) => {
            const practiceAreaId = orgUnitCascaderFromValue(organisationalUnit);
            const newFormValues = {
              templateId,
              client: client,
              pricingLevel,
              pricingStructure: pricingStructureToCascaderValue({
                pricingType,
                pricingLevel,
                pricingTarget: pricingTarget ?? PricingTarget.Fee,
              }),
              practiceAreaId,
              leadFeeEarner: cascaderValueFromFeeEarner(leadFeeEarner),
            };

            form.setFieldsValue(newFormValues);

            setScreen(Screens.matterDetailsSelection);
          },
          form,
        });
      },
    },
    [Screens.matterDetailsSelection]: {
      render: () =>
        renderMatterDetailsForm({
          form,
          showPricingOptions: MatterStartingOption === MatterStartingOptions.Bespoke,
        }),
    },
    [Screens.multiDisciplinaryMatter]: {
      render: () => renderMultiDisc({ form }),
    },
  };

  const renderModalFooter = () => (
    <>
      <Button
        onClick={() => {
          resetState();
          setDisplayModal(false);
          onCancel?.();
        }}
      >
        Cancel
      </Button>
      {!isFirstScreen(screen) && (
        <Button
          onClick={() => {
            const startingPoint = form.getFieldValue("startingPoint");
            setScreen(getPreviousScreen(screen, startingPoint));
          }}
        >
          Previous
        </Button>
      )}
      {screen !== Screens.MatterTemplateSelection && (
        <Button
          type="primary"
          onClick={() => {
            form
              .validateFields()
              .then(() => {
                const values = form.getFieldsValue(true);
                setScreen(getNextScreen(screen, values.startingPoint));
                if (isLastScreen(screen)) {
                  if (screen === Screens.multiDisciplinaryMatter) {
                    resetState();
                    createMultiDiscMatter({
                      clientId: values.client.id,
                      phases: values.parentPhases,
                      title: values.multiDiscTitle,
                    });
                    setDisplayModal(false);
                  }
                  resetState();
                  onCreate(convertToCreateMatter(values));
                  setDisplayModal(false);
                }
              })
              .catch((e) => {
                console.log("error", { e });
              });
          }}
        >
          Next
        </Button>
      )}
    </>
  );
  return (
    <>
      <Button
        onClick={() => {
          setDisplayModal(true);
        }}
        type="primary"
        loading={loading}
      >
        {labelOveride || "Create Pricing Proposal"}
      </Button>
      <Modal
        open={displayModal}
        width="850px"
        closable={false}
        footer={renderModalFooter()}
        style={{ top: 0 }}
        bodyStyle={{ paddingBottom: 0 }}
      >
        {!!parentMatterId && <ParentPhaseSelect parentMatterId={parentMatterId} form={form} />}
        {ScreensFunctions[screen].render()}
      </Modal>
    </>
  );
};

function convertToCreateMatter<T extends MatterType, U extends MatterFormMode>(
  props: MatterFormValuesToData<T, U>
) {
  const convertedValues = formValuesToData(props, "matter" as MatterType, MatterFormMode.Create);

  return convertedValues as MatterNonExternalFieldsPreUpdateMatterCreate;
}
